import React, { useState, useEffect, useMemo } from 'react';
import { Box, Grid, Alert, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  DateRangeSelector,
  UsageMetricCard,
  UsageByEndpointChart,
  RefreshButton,
  LoadingOverlay,
  UsageSkeleton,
} from '../Components';
import { useApiData } from '../hooks/useApiData';
import { formatUsd } from '../utils/formatUsd';

const ApiUsagePage = () => {
  const [dateRange, setDateRange] = useState('30');
  const {
    usageData,
    loading,
    updatingUsage,
    refreshing,
    error,
    updateUsage,
    refreshData,
  } = useApiData();

  // Calculate period based on selected date range
  const getPeriod = (days: string) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - parseInt(days));

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Fetch usage data when date range changes
  useEffect(() => {
    updateUsage(parseInt(dateRange));
  }, [dateRange]);

  // Calculate metrics from API data
  const period = getPeriod(dateRange);
  const totalCost = usageData ? formatUsd(usageData.total_cost) : formatUsd(0);
  const totalApiCalls = usageData?.total_usage_count?.toString() || '0';

  const callHistoryRows = useMemo(() => {
    const h = usageData?.call_history;
    if (!h?.length) return [];
    return h.map((r, i) => ({ id: `${r.timestamp}-${i}`, ...r }));
  }, [usageData?.call_history]);

  const callHistoryColumns = useMemo(
    (): GridColDef[] => [
      {
        field: 'timestamp',
        headerName: 'Time',
        width: 190,
        valueFormatter: params => {
          const ts = params.value as number;
          if (ts == null || Number.isNaN(ts)) return '';
          return new Date(ts * 1000).toLocaleString(undefined, {
            dateStyle: 'short',
            timeStyle: 'short',
          });
        },
      },
      { field: 'route_key', headerName: 'Route', flex: 1, minWidth: 180 },
      {
        field: 'cost',
        headerName: 'Cost',
        width: 100,
        valueFormatter: params => formatUsd(Number(params.value ?? 0)),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 100,
        valueFormatter: params => {
          const s = params.value as string;
          return s === 'failed' ? 'Failed' : 'Success';
        },
      },
      {
        field: 'deny_reason',
        headerName: 'Reason',
        flex: 1,
        minWidth: 140,
        valueFormatter: params => {
          const v = params.value as string | undefined;
          return v && String(v).trim() ? v : '—';
        },
      },
    ],
    []
  );

  if (loading) {
    return <UsageSkeleton />;
  }

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <RefreshButton onRefresh={refreshData} loading={refreshing} />
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Grid container spacing={3}>
        {/* Date Range Selector */}
        <Grid item xs={12} md={4}>
          <DateRangeSelector value={dateRange} onChange={setDateRange} />
        </Grid>

        {/* Metric Cards Row */}
        <Grid item xs={12}>
          <LoadingOverlay loading={updatingUsage}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <UsageMetricCard title="Period" value={period} />
              </Grid>
              <Grid item xs={12} md={3}>
                <UsageMetricCard title="Total Cost" value={totalCost} />
              </Grid>
              <Grid item xs={12} md={3}>
                <UsageMetricCard title="Total API Calls" value={totalApiCalls} />
              </Grid>
            </Grid>
          </LoadingOverlay>
        </Grid>

        {/* Usage by Endpoint Chart */}
        <Grid item xs={12}>
          <LoadingOverlay loading={updatingUsage}>
            <UsageByEndpointChart
              data={
                usageData || {
                  grouped_usage_data: {},
                  total_usage_count: 0,
                  total_cost: 0,
                }
              }
            />
          </LoadingOverlay>
        </Grid>

        <Grid item xs={12}>
          <LoadingOverlay loading={updatingUsage}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Call history
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Successful and failed attempts (newest first, up to 1,000 rows). Chart and totals above
              count successful billable calls only.
            </Typography>
            <Box sx={{ width: '100%', height: 420 }}>
              <DataGrid
                rows={callHistoryRows}
                columns={callHistoryColumns}
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                disableSelectionOnClick
                density="compact"
                hideFooter={callHistoryRows.length <= 25}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  '& .MuiDataGrid-columnHeaders': { fontWeight: 600 },
                }}
              />
            </Box>
          </LoadingOverlay>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ApiUsagePage;
